// Auswahl der Themen-Kategorien — EINE Komponente für AudioForm, VideoForm und WebComponentForm.
//
// ⚠️ Vorher lag dieser Block als drei zeichengleiche Kopien in den Formularen, jede an das
// hartkodierte Enum gebunden. Jetzt eine Komponente auf einer Datenquelle. Dadurch existiert
// insbesondere die Behandlung unbekannter Slugs an genau EINER Stelle.
// Plan: docs/plans/plan-themen-kategorien-2026-08.md
import React, { useState } from 'react';
import { useTopicTaxonomy } from '../../hooks/useTopicTaxonomy';

interface TopicCategoryPickerProps {
  /** Aktuell gewählte Unterthemen-Slugs (`content.metadata.topicCategories`). */
  selected: string[];
  /** Ein Slug wurde an- oder abgewählt. */
  onToggle: (subTopicKey: string) => void;
}

export const TopicCategoryPicker: React.FC<TopicCategoryPickerProps> = ({ selected, onToggle }) => {
  const { mainKeys, subKeysFor, labelFor, deactivatedSelected, unknownSelected, isLoading, error, reload } =
    useTopicTaxonomy();
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpand = (mainKey: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(mainKey)) next.delete(mainKey);
      else next.add(mainKey);
      return next;
    });
  };

  if (isLoading) {
    return (
      <div className="border border-secondary-200 rounded-lg p-4">
        <p className="text-sm text-secondary-500">Themen-Kategorien werden geladen…</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="border border-error-200 bg-error-50 rounded-lg p-4">
        <p className="text-sm text-error-600">{error}</p>
        <button type="button" onClick={reload} className="btn btn-outline btn-sm mt-3">
          Erneut versuchen
        </button>
      </div>
    );
  }

  const veraltet = deactivatedSelected(selected);
  const verwaist = unknownSelected(selected);

  return (
    <div className="space-y-2 border border-secondary-200 rounded-lg p-4 max-h-96 overflow-y-auto">
      {/* Bereits gewählte Themen, die inzwischen deaktiviert wurden. Sie stehen HIER und nicht in
          der Liste unten: neu wählbar dürfen sie nicht sein — sonst bewirkte das Deaktivieren
          nichts —, verschweigen dürfen wir sie aber ebenso wenig, sonst fiele die Zuordnung beim
          nächsten Speichern still weg. */}
      {veraltet.length > 0 && (
        <div className="border border-warning-200 bg-warning-50 rounded p-3 mb-2">
          <p className="text-xs text-warning-800 mb-2">
            {veraltet.length === 1 ? 'Ein zugeordnetes Thema ist' : `${veraltet.length} zugeordnete Themen sind`}{' '}
            deaktiviert und nicht mehr wählbar. Die Zuordnung bleibt erhalten, bis Sie sie abwählen.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {veraltet.map((key) => (
              <label
                key={key}
                className="flex items-center p-2 border border-warning-300 bg-white rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked
                  onChange={() => onToggle(key)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded mr-2"
                />
                <span className="text-sm text-secondary-500">{labelFor(key)}</span>
                <span className="ml-2 text-xs bg-warning-100 text-warning-800 px-2 py-0.5 rounded">veraltet</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Slugs, die am Inhalt hängen, die Taxonomie aber nicht kennt.
          Sie MÜSSEN sichtbar sein: der Speicherpfad reicht `topicCategories` ungeprüft durch —
          würde das Formular sie verschweigen, verschwänden sie beim nächsten Speichern still. */}
      {verwaist.length > 0 && (
        <div className="border border-warning-200 bg-warning-50 rounded p-3 mb-2">
          <p className="text-xs text-warning-800 mb-2">
            {verwaist.length === 1 ? 'Eine Zuordnung ist' : `${verwaist.length} Zuordnungen sind`} in den
            Themen-Kategorien nicht mehr vorhanden.{' '}
            {verwaist.length === 1 ? 'Die Zuordnung bleibt' : 'Die Zuordnungen bleiben'} erhalten, bis Sie sie abwählen.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {verwaist.map((key) => (
              <label
                key={key}
                className="flex items-center p-2 border border-warning-300 bg-white rounded cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked
                  onChange={() => onToggle(key)}
                  className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded mr-2"
                />
                <span className="text-sm font-mono">{key}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      {/* Seit die Auswahl nur Aktives zeigt, ist „leer" nicht mehr gleichbedeutend mit „nichts
          gepflegt" — ohne diesen Hinweis stünde hier ein erklärungsloser leerer Kasten. */}
      {mainKeys.length === 0 && (
        <p className="text-sm text-secondary-500">
          Keine wählbaren Themen-Kategorien — alle sind deaktiviert oder es ist noch keine angelegt.
        </p>
      )}

      {mainKeys.map((mainKey) => {
        const subKeys = subKeysFor(mainKey);
        const isExpanded = expanded.has(mainKey);
        const selectedHere = selected.filter((s) => subKeys.includes(s));

        return (
          <div key={mainKey} className="border-b border-secondary-100 last:border-b-0 pb-2 last:pb-0">
            <button
              type="button"
              onClick={() => toggleExpand(mainKey)}
              className="w-full flex items-center justify-between p-2 hover:bg-secondary-50 rounded transition-colors"
            >
              <span className="font-semibold text-secondary-900">{labelFor(mainKey)}</span>
              <div className="flex items-center space-x-2">
                {selectedHere.length > 0 && (
                  <span className="text-xs bg-primary-100 text-primary-700 px-2 py-1 rounded">
                    {selectedHere.length} ausgewählt
                  </span>
                )}
                <span className={`transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}>▼</span>
              </div>
            </button>

            {isExpanded && (
              <div className="mt-2 ml-4 grid grid-cols-1 md:grid-cols-2 gap-2">
                {subKeys.map((subKey) => {
                  const isSelected = selected.includes(subKey);
                  return (
                    <label
                      key={subKey}
                      className={`flex items-center p-2 border rounded cursor-pointer transition-colors ${
                        isSelected
                          ? 'bg-primary-50 border-primary-500 text-primary-700'
                          : 'bg-white border-secondary-300 hover:border-secondary-400'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => onToggle(subKey)}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-secondary-300 rounded mr-2"
                      />
                      <span className="text-sm">{labelFor(subKey)}</span>
                    </label>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
